import React, { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signOut,
  User,
  sendPasswordResetEmail,
  getRedirectResult,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { auth, db } from "../../firebase/config";
import { getFunctions, httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/config";

interface UserProfile {
  uid: string;
  email: string;
  fullName?: string;
  displayName?: string;
  photoURL?: string;
  credits: number;
  projectCount: number;
  createdAt: Date;
  updatedAt: Date;
  onboardingCompleted: boolean;
  lastLoginAt: Date;
  stripeCustomerId?: string;
  referralCode?: string;
  referredBy?: string | null;
  hasCompletedFirstPurchase?: boolean;
  themePreference?: "light" | "dark" | "system";
  lastSeenNotificationVersion?: number;
  useCase?: string;
  role?: string;

  // --- NEW SUBSCRIPTION FIELDS ---
  subscriptionTier?: "free" | "hobbyist" | "pro" | "enterprise";
  subscriptionStatus?: "active" | "past_due" | "canceled" | "incomplete";
  stripeSubscriptionId?: string;

  // Daily & Monthly counters
  dailyCredits?: number;
  monthlyFreeCreditsUsed?: number;

  // Timestamps (can be Firestore Timestamp or Date depending on sync)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lastDailyReset?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lastMonthlyReset?: any;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  needsEmailVerification: boolean;
  logout: () => Promise<void>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
  completeOnboarding: (
    fullName: string,
    additionalData?: {
      referralCode: string;
      referredBy: string | null;
      credits: number;
      hasCompletedFirstPurchase: boolean;
    },
  ) => Promise<void>;
  useCredit: () => Promise<boolean>;
  resetPassword: (email: string) => Promise<void>;
  markNotificationAsSeen: (version: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: null,
  loading: true,
  needsEmailVerification: false,
  logout: async () => {},
  updateUserProfile: async () => {},
  completeOnboarding: async () => {},
  useCredit: async () => false,
  resetPassword: async () => {},
  markNotificationAsSeen: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

// Helper function to generate referral code
const generateReferralCode = (uid: string): string => {
  const base = uid.substring(0, 8).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${base}${random}`;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);

  const createOrGetUserProfile = async (user: User): Promise<UserProfile> => {
    const userDocRef = doc(db, "users", user.uid);

    try {
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const profileData = userDoc.data() as UserProfile;

        const displayName =
          user.displayName || profileData.displayName || profileData.fullName;

        // DON'T update photoURL - let users keep their custom uploaded photo
        await updateDoc(userDocRef, {
          lastLoginAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ...(displayName && { displayName }),
          // Remove the photoURL update completely
        });

        return {
          ...profileData,
          displayName,
          lastLoginAt: new Date(),
          updatedAt: new Date(),
        };
      } else {
        const now = new Date();
        const referralCode = generateReferralCode(user.uid);

        const newProfile: UserProfile = {
          uid: user.uid,
          email: user.email || "",
          fullName: undefined,
          displayName: user.displayName || undefined,
          photoURL: user.photoURL || undefined,
          credits: 0,
          dailyCredits: 5, // Start with 5 daily credits
          projectCount: 0,
          createdAt: now,
          updatedAt: now,
          onboardingCompleted: false,
          lastLoginAt: now,
          themePreference: "system",
          referralCode: referralCode,
          referredBy: null,
        };

        // Create Stripe customer
        let stripeCustomerId: string | undefined;
        try {
          const createStripeCustomer = httpsCallable(
            functions,
            "createStripeCustomer",
          );
          const result = await createStripeCustomer({
            email: user.email,
            uid: user.uid,
          });
          stripeCustomerId = (result.data as any).customerId;
        } catch (stripeError) {
          // console.error("Error creating Stripe customer:", stripeError);
        }

        // try {
        //   const addToSendGrid = httpsCallable(functions, "addUserToSendGrid");
        //   await addToSendGrid({
        //     email: user.email,
        //     firstName: user.displayName?.split(" ")[0] || "",
        //     lastName: user.displayName?.split(" ").slice(1).join(" ") || "",
        //   });
        // } catch (sendGridError) {
        //   console.error("Error adding user to SendGrid:", sendGridError);
        // }

        const newProfileData: any = {
          uid: user.uid,
          email: user.email || "",
          credits: 0,
          dailyCredits: 5, // Start with 5 daily credits
          projectCount: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          onboardingCompleted: false,
          lastLoginAt: serverTimestamp(),
          themePreference: "system",
          referralCode: referralCode,
          referredBy: null,
        };

        if (user.displayName) {
          newProfileData.displayName = user.displayName;
        }

        if (user.photoURL) {
          newProfileData.photoURL = user.photoURL;
        }

        if (stripeCustomerId) {
          newProfileData.stripeCustomerId = stripeCustomerId;
          newProfile.stripeCustomerId = stripeCustomerId;
        }

        await setDoc(userDocRef, newProfileData);

        const verifyDoc = await getDoc(userDocRef);
        if (!verifyDoc.exists()) {
          // console.error("ERROR: Document was not created in Firestore");
        }

        return newProfile;
      }
    } catch (error: any) {
      if (
        error.code === "unavailable" ||
        error.code === "failed-precondition"
      ) {
        // console.warn("Firestore temporarily unavailable, retrying...");

        await new Promise((resolve) => setTimeout(resolve, 1000));

        try {
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const profileData = userDoc.data() as UserProfile;
            return {
              ...profileData,
              lastLoginAt: new Date(),
              updatedAt: new Date(),
            };
          }
        } catch (retryError) {
          // console.error("Retry failed:", retryError);
        }
      }

      // console.error("Error in createOrGetUserProfile:", error);
      throw error;
    }
  };

  useEffect(() => {
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          // onAuthStateChanged will pick this up automatically
        }
      })
      .catch((err) => {
        console.error("Microsoft redirect error:", err.message);
      });
  }, []);

  useEffect(() => {
    async function triggerDailyCheck() {
      if (user) {
        const functions = getFunctions();
        const checkDaily = httpsCallable(functions, "checkDailyRefill");
        try {
          await checkDaily();
          // Optionally refresh user data here to see the new 5 credits immediately
        } catch (error) {
          console.error("Failed to check daily credits", error);
        }
      }
    }

    triggerDailyCheck();
  }, [user]); // Runs whenever the user logs in or the user object updates

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          await user.getIdToken(true);

          // Check if this is an email/password user who hasn't verified
          const isEmailPasswordUser = user.providerData.some(
            (provider) => provider.providerId === "password",
          );

          if (isEmailPasswordUser && !user.emailVerified) {
            setUser(user);
            setUserProfile(null);
            setNeedsEmailVerification(true);
            setLoading(false);
            return;
          }

          // Email is verified (or OAuth user), proceed normally
          setNeedsEmailVerification(false);
          const profile = await createOrGetUserProfile(user);
          setUser(user);
          setUserProfile(profile);
        } catch (error) {
          // console.error("Error loading user profile:", error);
          setUser(user);
          setUserProfile(null);
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setNeedsEmailVerification(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Force profile refresh when returning from Stripe
  useEffect(() => {
    const checkStripeReturn = async () => {
      const searchParams = new URLSearchParams(window.location.search);

      if (
        searchParams.has("success") &&
        searchParams.get("type") === "credits" &&
        user
      ) {
        await new Promise((resolve) => setTimeout(resolve, 3000));

        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const freshData = userDoc.data() as UserProfile;

            setUserProfile((prev) =>
              prev
                ? {
                    ...prev,
                    credits: freshData.credits,
                    hasCompletedFirstPurchase:
                      freshData.hasCompletedFirstPurchase,
                    updatedAt: new Date(),
                  }
                : null,
            );
          }
        } catch (error) {
          // console.error("Failed to refresh profile after Stripe:", error);
        }
      }
    };

    checkStripeReturn();
  }, [user]);

  const updateUserProfile = async (updates: Partial<UserProfile>) => {
    if (!user || !userProfile) return;

    const userDocRef = doc(db, "users", user.uid);
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp(),
    };

    // console.log("💾 Updating Firestore user profile:", updateData);

    await updateDoc(userDocRef, updateData);

    // Update local state
    setUserProfile((prev) =>
      prev
        ? {
            ...prev,
            ...updates,
            updatedAt: new Date(),
          }
        : null,
    );

    // console.log("✅ Local userProfile state updated");
  };

  const completeOnboarding = async (
    fullName: string,
    additionalData?: {
      referralCode: string;
      referredBy: string | null;
      credits: number;
      hasCompletedFirstPurchase: boolean;
    },
  ) => {
    if (!user) return;

    const userDocRef = doc(db, "users", user.uid);

    const updateData: any = {
      fullName: fullName.trim(),
      displayName: fullName.trim(),
      onboardingCompleted: true,
      updatedAt: serverTimestamp(),
    };

    if (additionalData) {
      updateData.referralCode = additionalData.referralCode;
      updateData.referredBy = additionalData.referredBy;
      updateData.credits = additionalData.credits;
      updateData.hasCompletedFirstPurchase =
        additionalData.hasCompletedFirstPurchase;
    }

    await updateDoc(userDocRef, updateData);

    setUserProfile((prev) =>
      prev
        ? {
            ...prev,
            fullName: fullName.trim(),
            displayName: fullName.trim(),
            onboardingCompleted: true,
            ...(additionalData && {
              referralCode: additionalData.referralCode,
              referredBy: additionalData.referredBy,
              credits: additionalData.credits,
              hasCompletedFirstPurchase:
                additionalData.hasCompletedFirstPurchase,
            }),
            updatedAt: new Date(),
          }
        : null,
    );
  };

  const useCredit = async (): Promise<boolean> => {
    if (!user || !userProfile) return false;

    const userDocRef = doc(db, "users", user.uid);

    try {
      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userDocRef);
        const userData = userDoc.data() as UserProfile;

        if (userData.credits <= 0) {
          throw new Error("Insufficient credits");
        }

        transaction.update(userDocRef, {
          credits: userData.credits - 1,
          updatedAt: serverTimestamp(),
        });
      });

      setUserProfile((prev) =>
        prev
          ? {
              ...prev,
              credits: prev.credits - 1,
              updatedAt: new Date(),
            }
          : null,
      );

      return true;
    } catch (error) {
      // console.error("Error using credit:", error);
      return false;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      // console.error("Error sending password reset email:", error);
      throw error;
    }
  };

  const markNotificationAsSeen = async (version: number) => {
    if (!user) return;

    const userDocRef = doc(db, "users", user.uid);
    await updateDoc(userDocRef, {
      lastSeenNotificationVersion: version,
      updatedAt: serverTimestamp(),
    });

    setUserProfile((prev) =>
      prev
        ? {
            ...prev,
            lastSeenNotificationVersion: version,
            updatedAt: new Date(),
          }
        : null,
    );
  };

  const logout = async () => {
    await signOut(auth);
  };

  const value = {
    user,
    userProfile,
    loading,
    needsEmailVerification,
    logout,
    updateUserProfile,
    completeOnboarding,
    useCredit,
    resetPassword,
    markNotificationAsSeen,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
